import * as React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { updateUserPermissions } from '../../lib/services/adminService';
import { IUser, Permission, PERMISSIONS } from '../../lib/interfaces';

import {
  exportButtonStyle,
  exportButtonStyleDisabled,
} from '../PatientDetails/PatientDetails';

type Props = {
  target: Pick<
    IUser,
    '_id' | 'isAdmin' | 'permissions' | 'firstName' | 'lastName'
  >;

  onUpdated?: (nextPermissions: Permission[]) => void;
  disabled?: boolean;
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: { maxHeight: ITEM_HEIGHT * 7 + ITEM_PADDING_TOP, width: 340 },
  },
};

export default function AdminPermissionsEditor({
  target,
  onUpdated,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const [value, setValue] = React.useState<Permission[]>(
    target.permissions ?? []
  );
  const [saving, setSaving] = React.useState(false);
  const norm = (arr?: Permission[]) =>
    (arr ?? []).filter((v, i, a) => a.indexOf(v) === i).sort();

  const initial = React.useRef(JSON.stringify(norm(target.permissions)));
  const changed = JSON.stringify(norm(value)) !== initial.current;

  const handleSave = async () => {
    try {
      setSaving(true);
      const unique = Array.from(new Set(value));
      await updateUserPermissions(target._id, unique);
      initial.current = JSON.stringify([...unique].sort());
      onUpdated?.(unique);
    } finally {
      setSaving(false);
    }
  };

  if (!target.isAdmin) return null;

  const tp = (perm: Permission) =>
    t(`permission.${perm}`, { nsSeparator: false });

  const buttonsDisabled = !changed || saving;
  return (
    <Box sx={{ mt: 1, ml: 1 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <FormControl fullWidth sx={{ flex: 1, minWidth: 320 }} size="medium">
          <InputLabel id="perm-label">{t('permissions')}</InputLabel>
          <Select<Permission[]>
            multiple
            labelId="perm-label"
            value={value}
            onChange={(e) => setValue(e.target.value as Permission[])}
            input={<OutlinedInput label={t('permissions')} />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((p) => (
                  <Chip key={p} label={tp(p)} />
                ))}
              </Box>
            )}
            MenuProps={MenuProps}
            disabled={disabled || saving}
            size="medium"
          >
            {PERMISSIONS.map((p) => (
              <MenuItem key={p} value={p}>
                <Checkbox checked={value.indexOf(p) > -1} />
                <ListItemText primary={tp(p)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            ml: 'auto',
            pt: 0.5,
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={buttonsDisabled}
            sx={{
              ...(buttonsDisabled
                ? exportButtonStyleDisabled
                : exportButtonStyle),
              m: 0,
              px: 2.5,
              py: 1,
            }}
          >
            {t('save')}
          </Button>
          <Button
            variant="text"
            size="large"
            onClick={() => setValue(JSON.parse(initial.current))}
            disabled={buttonsDisabled}
            sx={{
              px: 2.5,
              py: 1,
            }}
          >
            {t('reset')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
